import React, { useRef } from 'react';
import '../../css/Tabs.css';

/**
 * Tabs — accessible tablist for splitting a long panel into peers.
 *
 * Renders the tablist only; the host renders the matching panel and pairs it up
 * with `tabPanelProps(idPrefix, id)`. That keeps the panel's scroll container
 * under the host's control, which is what makes a tabbed rail worth having.
 *
 * Follows the ARIA tabs pattern: exactly one tab in the tab order (roving
 * tabindex), ←/→ to move between tabs, Home/End to jump to the ends.
 *
 * Props:
 *   tabs      — [{ id, label, count, dot, title }]
 *               `count` renders a badge (0 renders as a muted zero, not hidden,
 *               so "no notes" and "notes not loaded" don't look identical).
 *               `dot` renders a small presence marker instead.
 *   value     — id of the active tab
 *   onChange  — (id) => void
 *   idPrefix  — namespace for the generated aria ids
 */
const Tabs = ({ tabs = [], value, onChange, idPrefix = 'qdtab', label, className = '' }) => {
    const refs = useRef([]);

    const move = (index) => {
        const tab = tabs[index];
        if (!tab) return;
        onChange?.(tab.id);
        refs.current[index]?.focus();
    };

    const handleKeyDown = (e, index) => {
        switch (e.key) {
            case 'ArrowRight': e.preventDefault(); move((index + 1) % tabs.length); break;
            case 'ArrowLeft':  e.preventDefault(); move((index - 1 + tabs.length) % tabs.length); break;
            case 'Home':       e.preventDefault(); move(0); break;
            case 'End':        e.preventDefault(); move(tabs.length - 1); break;
            default: break;
        }
    };

    return (
        <div className={`qd-tabs ${className}`.trim()} role="tablist" aria-label={label} aria-orientation="horizontal">
            {tabs.map((tab, index) => {
                const active = tab.id === value;
                return (
                    <button
                        key={tab.id}
                        ref={(el) => { refs.current[index] = el; }}
                        type="button"
                        role="tab"
                        id={`${idPrefix}-tab-${tab.id}`}
                        aria-selected={active}
                        aria-controls={`${idPrefix}-panel-${tab.id}`}
                        tabIndex={active ? 0 : -1}
                        title={tab.title}
                        className={`qd-tab ${active ? 'is-active' : ''}`.trim()}
                        onClick={() => onChange?.(tab.id)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                    >
                        <span className="qd-tab-label">{tab.label}</span>
                        {typeof tab.count === 'number' && (
                            <span className="qd-tab-count" aria-hidden="true">{tab.count}</span>
                        )}
                        {tab.dot && <span className="qd-tab-dot" aria-hidden="true" />}
                    </button>
                );
            })}
        </div>
    );
};

/** Spread onto the host's panel element so it announces as this tab's panel. */
export const tabPanelProps = (idPrefix, id) => ({
    role: 'tabpanel',
    id: `${idPrefix}-panel-${id}`,
    'aria-labelledby': `${idPrefix}-tab-${id}`,
    tabIndex: 0,
});

export default Tabs;
